import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { LogOut, Plus, MapPin, Clock, Pencil, Trash2, UserPlus } from 'lucide-react';
import ComboboxClientes from '../components/ComboboxClientes';

const DashboardComercial = () => {
  const { user, logout, API } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAccion, setEditingAccion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [formData, setFormData] = useState({
    cliente_id: '',
    es_posible_cliente: false,
    posible_cliente_nombre: '',
    action_type_id: '',
    duracion_horas: 0,
    duracion_minutos: 30,
    descripcion: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientesRes, actionTypesRes, accionesRes] = await Promise.all([
        axios.get(`${API}/clientes`),
        axios.get(`${API}/action-types`),
        axios.get(`${API}/acciones`)
      ]);
      setClientes(clientesRes.data);
      setActionTypes(actionTypesRes.data);
      setAcciones(accionesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalización no soportada'));
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }),
        (error) => reject(error)
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const location = await getCurrentLocation();
      const payload = {
        cliente_id: formData.es_posible_cliente ? null : formData.cliente_id,
        es_posible_cliente: formData.es_posible_cliente,
        posible_cliente_nombre: formData.es_posible_cliente ? formData.posible_cliente_nombre : null,
        action_type_id: formData.action_type_id,
        duracion_horas: formData.duracion_horas,
        duracion_minutos: formData.duracion_minutos,
        descripcion: formData.descripcion,
        lat: location.lat,
        lng: location.lng
      };
      await axios.post(`${API}/acciones`, payload);
      toast.success('Acción comercial registrada');
      setFormData({
        cliente_id: '',
        es_posible_cliente: false,
        posible_cliente_nombre: '',
        action_type_id: '',
        duracion_horas: 0,
        duracion_minutos: 30,
        descripcion: ''
      });
      fetchData();
    } catch (error) {
      if (error.message === 'Geolocalización no soportada') {
        toast.error('Tu dispositivo no soporta geolocalización');
      } else if (error.code === 1) {
        toast.error('Permiso de ubicación denegado');
      } else {
        toast.error(error.response?.data?.detail || 'Error al registrar acción');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (accion) => {
    setEditingAccion(accion);
    setShowEditModal(true);
  };

  const handleUpdateAccion = async () => {
    try {
      await axios.put(`${API}/acciones/${editingAccion.id}`, {
        cliente_id: editingAccion.es_posible_cliente ? null : editingAccion.cliente_id,
        es_posible_cliente: editingAccion.es_posible_cliente || false,
        posible_cliente_nombre: editingAccion.es_posible_cliente ? editingAccion.posible_cliente_nombre : null,
        action_type_id: editingAccion.action_type_id,
        duracion_horas: editingAccion.duracion_horas,
        duracion_minutos: editingAccion.duracion_minutos,
        descripcion: editingAccion.descripcion
      });
      toast.success('Acción actualizada');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar acción');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta acción?')) return;
    try {
      await axios.delete(`${API}/acciones/${id}`);
      toast.success('Acción eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar acción');
    }
  };

  const getBadgeClass = (actionTypeName) => {
    const lower = actionTypeName.toLowerCase();
    if (lower.includes('visita')) return 'badge-visit';
    if (lower.includes('llamada')) return 'badge-call';
    if (lower.includes('email')) return 'badge-email';
    return '';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">FieldFlow</h1>
            <p className="text-sm text-muted-foreground">Hola, {user.nombre}</p>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-2xl font-semibold mb-6">Nueva Acción Comercial</h2>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="action-form">
            {/* Checkbox para Posible Cliente */}
            <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="es_posible_cliente"
                checked={formData.es_posible_cliente}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  es_posible_cliente: checked,
                  cliente_id: checked ? '' : formData.cliente_id
                })}
                data-testid="posible-cliente-checkbox"
              />
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-600" />
                <Label htmlFor="es_posible_cliente" className="text-sm font-medium cursor-pointer">
                  Es un posible cliente (aún no está en la lista)
                </Label>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {formData.es_posible_cliente ? (
                <div className="space-y-2">
                  <Label>Nombre del Posible Cliente *</Label>
                  <Input
                    value={formData.posible_cliente_nombre}
                    onChange={(e) => setFormData({ ...formData, posible_cliente_nombre: e.target.value })}
                    placeholder="Escribe el nombre del posible cliente"
                    className="h-12"
                    required
                    data-testid="posible-cliente-nombre-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <ComboboxClientes
                    clientes={clientes}
                    value={formData.cliente_id}
                    onChange={(value) => setFormData({ ...formData, cliente_id: value })}
                    data-testid="action-cliente-select"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Tipo de Acción *</Label>
                <Select
                  value={formData.action_type_id}
                  onValueChange={(value) => setFormData({ ...formData, action_type_id: value })}
                >
                  <SelectTrigger className="h-12" data-testid="action-type-select">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Horas</Label>
                <Select
                  value={formData.duracion_horas.toString()}
                  onValueChange={(value) => setFormData({ ...formData, duracion_horas: parseInt(value) })}
                >
                  <SelectTrigger className="h-12" data-testid="duration-hours-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(13)].map((_, i) => (
                      <SelectItem key={i} value={i.toString()}>{i}h</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Minutos</Label>
                <Select
                  value={formData.duracion_minutos.toString()}
                  onValueChange={(value) => setFormData({ ...formData, duracion_minutos: parseInt(value) })}
                >
                  <SelectTrigger className="h-12" data-testid="duration-minutes-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((min) => (
                      <SelectItem key={min} value={min.toString()}>{min}min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Describe la acción realizada..."
                rows={4}
                required
                data-testid="action-description-input"
              />
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading} data-testid="submit-action-button">
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Registrando...
                </span>
              ) : (
                <span className="flex items-center">
                  <Plus className="mr-2 h-4 w-4" />
                  Registrar Acción
                </span>
              )}
            </Button>
          </form>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-2xl font-semibold mb-6">Mis Acciones Comerciales</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Ubicación</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {acciones.map((accion) => (
                  <TableRow key={accion.id} className="table-row">
                    <TableCell className="table-cell">{formatDate(accion.fecha_hora)}</TableCell>
                    <TableCell className="table-cell font-medium">
                      {accion.es_posible_cliente ? (
                        <span className="flex items-center gap-1">
                          <UserPlus className="h-3 w-3 text-blue-600" />
                          <span className="text-blue-600">{accion.posible_cliente_nombre}</span>
                          <Badge variant="outline" className="ml-1 text-xs">Posible</Badge>
                        </span>
                      ) : (
                        accion.cliente_nombre
                      )}
                    </TableCell>
                    <TableCell className="table-cell">
                      <Badge className={getBadgeClass(accion.action_type_nombre)}>
                        {accion.action_type_nombre}
                      </Badge>
                    </TableCell>
                    <TableCell className="table-cell">
                      <span className="flex items-center text-sm">
                        <Clock className="h-3 w-3 mr-1" />
                        {accion.duracion_horas}h {accion.duracion_minutos}m
                      </span>
                    </TableCell>
                    <TableCell className="table-cell max-w-xs truncate">{accion.descripcion}</TableCell>
                    <TableCell className="table-cell">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${accion.lat},${accion.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="map-link inline-flex items-center text-accent"
                        data-testid={`map-link-${accion.id}`}
                      >
                        <MapPin className="h-4 w-4" />
                      </a>
                    </TableCell>
                    <TableCell className="table-cell">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(accion)}
                          data-testid={`edit-action-${accion.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(accion.id)}
                          data-testid={`delete-action-${accion.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {acciones.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No hay acciones registradas</p>
            )}
          </div>
        </div>
      </main>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent data-testid="edit-action-modal">
          <DialogHeader>
            <DialogTitle>Editar Acción</DialogTitle>
          </DialogHeader>
          {editingAccion && (
            <div className="space-y-4">
              {/* Checkbox para Posible Cliente */}
              <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Checkbox
                  id="edit_es_posible_cliente"
                  checked={editingAccion.es_posible_cliente || false}
                  onCheckedChange={(checked) => setEditingAccion({ 
                    ...editingAccion, 
                    es_posible_cliente: checked,
                    cliente_id: checked ? null : editingAccion.cliente_id
                  })}
                  data-testid="edit-posible-cliente-checkbox"
                />
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="edit_es_posible_cliente" className="text-sm font-medium cursor-pointer">
                    Es un posible cliente
                  </Label>
                </div>
              </div>

              {editingAccion.es_posible_cliente ? (
                <div className="space-y-2">
                  <Label>Nombre del Posible Cliente *</Label>
                  <Input
                    value={editingAccion.posible_cliente_nombre || ''}
                    onChange={(e) => setEditingAccion({ ...editingAccion, posible_cliente_nombre: e.target.value })}
                    placeholder="Nombre del posible cliente"
                    data-testid="edit-posible-cliente-nombre"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <ComboboxClientes
                    clientes={clientes}
                    value={editingAccion.cliente_id || ''}
                    onChange={(value) => setEditingAccion({ ...editingAccion, cliente_id: value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Tipo de Acción</Label>
                <Select
                  value={editingAccion.action_type_id}
                  onValueChange={(value) => setEditingAccion({ ...editingAccion, action_type_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Horas</Label>
                  <Select
                    value={editingAccion.duracion_horas.toString()}
                    onValueChange={(value) => setEditingAccion({ ...editingAccion, duracion_horas: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(13)].map((_, i) => (
                        <SelectItem key={i} value={i.toString()}>{i}h</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Minutos</Label>
                  <Select
                    value={editingAccion.duracion_minutos.toString()}
                    onValueChange={(value) => setEditingAccion({ ...editingAccion, duracion_minutos: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((min) => (
                        <SelectItem key={min} value={min.toString()}>{min}min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={editingAccion.descripcion}
                  onChange={(e) => setEditingAccion({ ...editingAccion, descripcion: e.target.value })}
                  rows={4}
                />
              </div>
              <Button onClick={handleUpdateAccion} className="w-full" data-testid="update-action-button">
                Actualizar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardComercial;
